import React, { useState, useMemo, useCallback } from 'react';

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AimOutlined,
  SoundOutlined,
  BulbOutlined,
  GiftOutlined,
  DesktopOutlined,
  PushpinOutlined,
  PushpinFilled,
  ThunderboltOutlined,
  SettingOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  PictureOutlined,
  UploadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  Button,
  Space,
  Popconfirm,
  Tooltip,
  Modal,
  Drawer,
  Form,
  Input,
  Select,
  Checkbox,
  Typography,
  Empty,
  Tag,
  Table,
  Radio,
  Slider,
  message,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useSearchParams } from 'react-router-dom';

import {
  LifeSphere,
  Theme,
  Subtheme,
  SemanticUnit,
  Angle,
  AngleType,
  ANGLE_TYPE_CONFIG,
  NodeStatus,
  NODE_STATUS_CONFIG,
  SmOffer,
  AdCabinet,
  Ad,
  AdSitelink,
  AdPlatform,
  AdStatus,
  YandexDirectGenerationSettings,
} from '../types';

import GenerateAdsModal from './GenerateAdsModal';
import AutoModeSetupModal from './AutoModeSetupModal';
import YandexDirectSettings, { getDefaultYandexDirectSettings } from './YandexDirectSettings';
import AdImageEditor, { CropModal } from './AdImageEditor';
import { AdImageSettings, DEFAULT_AD_IMAGE_SETTINGS } from '../types';
import { donskojSpheres1 } from '../data/donskojSpheres1';
import { donskojSpheres2 } from '../data/donskojSpheres2';
import { donskojSpheres3 } from '../data/donskojSpheres3';
import { useDb } from '../db/useDataService';
import * as ds from '../db/dataService';

const { Text } = Typography;
const { TextArea } = Input;

// ============================================================
// MOCK DATA
// ============================================================

export const mockSmOffers: SmOffer[] = [
  { id: '56', productId: '1', name: 'Регистрация на бесплатный вебинар', description: 'Бесплатный вебинар по основам маркетинга', bindings: [{ scope: 'product', scopeId: '1' }] },
  { id: '57', productId: '1', name: 'Покупка курса', description: 'Полный курс «Основы маркетинга» со скидкой', bindings: [{ scope: 'product', scopeId: '1' }] },
];

export const allConnectedCabinets: AdCabinet[] = [
  { id: '58', productId: '', platform: 'yandex_direct', name: 'Основной кабинет Директ', externalId: '12345678' },
  { id: '59', productId: '', platform: 'yandex_direct', name: 'Тестовый кабинет Директ', externalId: '87654321' },
  { id: '60', productId: '', platform: 'yandex_direct', name: 'Кабинет клиента А', externalId: '11223344' },
  { id: '61', productId: '', platform: 'vk', name: 'Кабинет VK Реклама', externalId: 'vk-001' },
  { id: '62', productId: '', platform: 'vk', name: 'VK Тестовый', externalId: 'vk-002' },
];

export const initialProductCabinetIds: Record<string, string[]> = {
  '1': ['58', '59', '61'],
};

export const mockSpheres: LifeSphere[] = [
  {
    id: '1', productId: '1',
    name: 'Здоровье и тело',
    description: 'Физическое здоровье, энергия, внешность, питание',
    themes: [
      {
        id: '2', sphereId: 's1',
        name: 'Вес',
        description: 'Похудение, набор массы, удержание результата',
        subthemes: [
          {
            id: '8', themeId: '2',
            name: 'Похудение',
            description: 'Снижение веса, борьба с лишними килограммами',
            units: [
              { id: '23', subthemeId: '8', name: 'Похудеть к лету', description: 'Срочное желание привести себя в форму к сезону', examples: ['Осталось 3 месяца до отпуска, а я +15 кг', 'Каждый год обещаю похудеть к лету'] },
              { id: '24', subthemeId: '8', name: 'Стыдно раздеваться на пляже', description: 'Стеснение своего тела', examples: ['Не хожу на пляж уже 3 года', 'Одеваю закрытый купальник чтобы скрыть живот'] },
              { id: '25', subthemeId: '8', name: 'Перепробовала все диеты', description: 'Разочарование от неэффективных методов', examples: ['Кето, интервальное, подсчёт калорий — ничего не работает', 'Сбрасываю 5 кг и набираю 7'] },
            ],
          },
          {
            id: '9', themeId: '2',
            name: 'Набор массы',
            description: 'Невозможность набрать вес, дефицит массы тела',
            units: [
              { id: '26', subthemeId: '9', name: 'Ем много но не могу набрать', description: 'Быстрый метаболизм, худоба', examples: ['Ем за двоих а вешу 50 кг', 'Выгляжу болезненно худой'] },
            ],
          },
          {
            id: '10', themeId: '2',
            name: 'Удержание веса',
            description: 'Вес вернулся после диеты, эффект йо-йо',
            units: [
              { id: '27', subthemeId: '10', name: 'Скинула 10 кг а они вернулись', description: 'Откат после диеты', examples: ['Каждый раз возвращается ещё больше', 'Боюсь что опять сорвусь'] },
              { id: '28', subthemeId: '10', name: 'Не могу удержать результат', description: 'Нет системы после похудения', examples: ['Худею — срываюсь — набираю. По кругу', 'Не знаю как питаться после диеты'] },
            ],
          },
        ],
      },
      {
        id: '3', sphereId: 's1',
        name: 'Энергия',
        description: 'Усталость, сон, продуктивность, жизненные силы',
        subthemes: [
          {
            id: '11', themeId: '3',
            name: 'Хроническая усталость',
            description: 'Постоянная нехватка сил без видимых причин',
            units: [
              { id: '29', subthemeId: '11', name: 'Просыпаюсь уже уставшей', description: 'Утренняя разбитость', examples: ['Будильник звонит — а сил встать нет', '8 часов сна, а ощущение что не спала'] },
              { id: '30', subthemeId: '11', name: 'После обеда хочу спать', description: 'Послеобеденный провал энергии', examples: ['К 15:00 мозг отключается', 'Засыпаю на рабочем месте после обеда'] },
            ],
          },
          {
            id: '12', themeId: '3',
            name: 'Сон',
            description: 'Бессонница, некачественный сон, нарушения режима',
            units: [
              { id: '31', subthemeId: '12', name: 'Не могу уснуть до 2 ночи', description: 'Бессонница и сбитый режим', examples: ['Ложусь в 23:00, а засыпаю в 2', 'Мозг не выключается — кручу мысли'] },
            ],
          },
        ],
      },
      {
        id: '4', sphereId: 's1',
        name: 'Пищеварение',
        description: 'Вздутие, непереносимости, дискомфорт ЖКТ',
        subthemes: [
          {
            id: '13', themeId: '4',
            name: 'Вздутие и дискомфорт',
            description: 'Газообразование и тяжесть после еды',
            units: [
              { id: '32', subthemeId: '13', name: 'Живот надувается после каждой еды', description: 'Постоянное вздутие', examples: ['После любой еды живот как шарик', 'Неудобно на людях — постоянные газы'] },
            ],
          },
          {
            id: '14', themeId: '4',
            name: 'Непереносимости',
            description: 'Реакции на продукты, аллергии, чувствительность',
            units: [
              { id: '33', subthemeId: '14', name: 'Не понимаю на что реагирует организм', description: 'Невозможность определить триггер', examples: ['Что-то ем не то, но не пойму что', 'Высыпания после еды, а от чего — не знаю'] },
            ],
          },
        ],
      },
      {
        id: '5', sphereId: 's1',
        name: 'Внешность',
        description: 'Кожа, волосы, старение, внешний вид',
        subthemes: [
          {
            id: '15', themeId: '5',
            name: 'Кожа',
            description: 'Акне, сухость, тусклость, высыпания',
            units: [
              { id: '34', subthemeId: '15', name: 'Акне в 30 лет', description: 'Проблемная кожа во взрослом возрасте', examples: ['Акне в 30 — что со мной не так?', 'Перепробовала все кремы — не помогает'] },
              { id: '35', subthemeId: '15', name: 'Тусклая кожа', description: 'Нездоровый цвет лица', examples: ['Выгляжу серой и уставшей', 'Тональник не спасает — кожа мёртвая'] },
            ],
          },
          {
            id: '16', themeId: '5',
            name: 'Волосы',
            description: 'Выпадение, ломкость, тусклость волос',
            units: [
              { id: '36', subthemeId: '16', name: 'Волосы лезут пучками', description: 'Интенсивное выпадение волос', examples: ['На расчёске остаётся пучок каждый раз', 'Волосы стали в два раза тоньше за год'] },
            ],
          },
          {
            id: '17', themeId: '5',
            name: 'Старение',
            description: 'Преждевременное старение, морщины, увядание',
            units: [
              { id: '37', subthemeId: '17', name: 'Выгляжу старше своих лет', description: 'Несоответствие внешности и возраста', examples: ['Мне 35 а дают 45', 'Морщины, мешки — как у бабушки'] },
            ],
          },
        ],
      },
      {
        id: '6', sphereId: 's1',
        name: 'Психика и еда',
        description: 'Эмоциональное переедание, отношения с едой, расстройства пищевого поведения',
        subthemes: [
          {
            id: '18', themeId: '6',
            name: 'Эмоциональное переедание',
            description: 'Заедание стресса, тревоги, скуки',
            units: [
              { id: '38', subthemeId: '18', name: 'Заедаю стресс', description: 'Еда как способ справиться с эмоциями', examples: ['После тяжёлого дня — пачка печенья', 'Не могу остановиться когда нервничаю'] },
              { id: '39', subthemeId: '18', name: 'Ненавижу себя после срыва', description: 'Чувство вины после переедания', examples: ['Объелась и рыдаю', 'Каждый раз обещаю что последний — и снова'] },
            ],
          },
          {
            id: '19', themeId: '6',
            name: 'Отношения с едой',
            description: 'Страх еды, орторексия, зацикленность на питании',
            units: [
              { id: '40', subthemeId: '19', name: 'Боюсь еды', description: 'Каждый продукт воспринимается как угроза', examples: ['Боюсь съесть лишнее', 'Считаю калории и схожу с ума'] },
              { id: '41', subthemeId: '19', name: 'Зацикленность на правильном питании', description: 'ПП как навязчивая идея', examples: ['Всё время думаю что можно есть а что нельзя', 'ПП превратилось в тюрьму'] },
            ],
          },
        ],
      },
      {
        id: '7', sphereId: 's1',
        name: 'Болезни и гормоны',
        description: 'Хронические заболевания, гормональные нарушения, иммунитет',
        subthemes: [
          {
            id: '20', themeId: '7',
            name: 'Гормональные нарушения',
            description: 'Щитовидка, ПМС, менопауза, гормональный дисбаланс',
            units: [
              { id: '42', subthemeId: '20', name: 'Щитовидка — вес не уходит', description: 'Гипотиреоз и невозможность похудеть', examples: ['Щитовидка не даёт похудеть что ни делай', 'Эндокринолог сказал что с моими гормонами сложно'] },
              { id: '43', subthemeId: '20', name: 'ПМС невыносимый', description: 'Тяжёлый предменструальный синдром', examples: ['За неделю до месячных превращаюсь в другого человека', 'ПМС — хочу есть всё подряд и плакать'] },
            ],
          },
          {
            id: '21', themeId: '7',
            name: 'Сахар и диабет',
            description: 'Предиабет, инсулинорезистентность, сахарный диабет',
            units: [
              { id: '44', subthemeId: '21', name: 'Предиабет — врач сказал менять питание', description: 'Медицинское предписание изменить рацион', examples: ['Сахар на границе, врач напугал', 'Если не изменю питание — будет диабет'] },
            ],
          },
          {
            id: '22', themeId: '7',
            name: 'Иммунитет',
            description: 'Частые болезни, слабый иммунитет, восстановление',
            units: [
              { id: '45', subthemeId: '22', name: 'Болею каждый месяц', description: 'Частые простуды и ОРВИ', examples: ['Ребёнок из сада — я сразу болею', 'Иммунитет на нуле, ловлю всё подряд'] },
            ],
          },
        ],
      },
    ],
  },
  ...donskojSpheres1,
  ...donskojSpheres2,
  ...donskojSpheres3,
];

export const getProductSpheres = (productId: string): LifeSphere[] =>
  mockSpheres.filter((s) => s.productId === productId);

export const mockAnglesData: Angle[] = [
  // 23: Похудеть к лету
  { id: '46', unitId: '23', type: 'pain', enabled: true, score: 92, reasoning: 'Сильнейший триггер — страх не успеть к сезону.' },
  { id: '47', unitId: '23', type: 'urgency', enabled: true, score: 88, reasoning: 'Дедлайн «лето» создаёт естественную срочность.' },
  { id: '48', unitId: '23', type: 'instruction', enabled: true, score: 75, reasoning: 'ЦА ищет конкретный план.' },
  // 24: Стыдно раздеваться
  { id: '49', unitId: '24', type: 'pain', enabled: true, score: 95, reasoning: 'Стыд тела — сильнейший эмоциональный триггер.' },
  { id: '50', unitId: '24', type: 'contrast', enabled: true, score: 82, reasoning: 'До/после показывает трансформацию.' },
  // 25: Перепробовала все диеты
  { id: '51', unitId: '25', type: 'provocation', enabled: true, score: 90, reasoning: '«Диеты не работают» — резонирует с ЦА.' },
  { id: '52', unitId: '25', type: 'social_proof', enabled: true, score: 85, reasoning: 'Статистика — мощный аргумент.' },
];

const AD_STATUS_CONFIG: Record<AdStatus, { label: string; color: string }> = {
  active: { label: 'Активно', color: 'green' },
  paused: { label: 'Пауза', color: 'orange' },
  draft: { label: 'Черновик', color: 'default' },
};

// --- Entity stats ---

interface EntityPeriodStats {
  spend: number;
  spendChange: number;
  profit: number;
  profitChange: number;
  romi: number;
  romiChange: number;
  cpl: number;
  cplChange: number;
}

interface EntityStats {
  campaigns: number;
  ads: number;
  budget: number;
  periods: {
    '7d': EntityPeriodStats;
    '14d': EntityPeriodStats;
    '30d': EntityPeriodStats;
    all: EntityPeriodStats;
  };
}

const PERIOD_LABELS: Record<string, string> = {
  '7d': '7 дней',
  '14d': '14 дней',
  '30d': '30 дней',
  all: 'Всё время',
};

const mkStats = (c: number, a: number, b: number, s7: number, p7: number, r7: number, cpl7: number): EntityStats => ({
  campaigns: c, ads: a, budget: b,
  periods: {
    '7d': { spend: s7, spendChange: 8.5, profit: p7, profitChange: 12.4, romi: r7, romiChange: 5.2, cpl: cpl7, cplChange: -3.5 },
    '14d': { spend: Math.round(s7 * 1.8), spendChange: 12.3, profit: Math.round(p7 * 1.9), profitChange: 18.2, romi: r7 + 30, romiChange: 8.1, cpl: Math.round(cpl7 * 0.95), cplChange: -5.2 },
    '30d': { spend: Math.round(s7 * 2.5), spendChange: 15.7, profit: Math.round(p7 * 3.1), profitChange: 22.5, romi: r7 + 60, romiChange: 11.3, cpl: Math.round(cpl7 * 0.9), cplChange: -7.8 },
    all: { spend: Math.round(s7 * 4.2), spendChange: 0, profit: Math.round(p7 * 4.5), profitChange: 0, romi: r7 + 40, romiChange: 0, cpl: Math.round(cpl7 * 0.93), cplChange: 0 },
  },
});

const mockEntityStats: Record<string, EntityStats> = {
  // Spheres
  '1': mkStats(5, 22, 8000, 56000, 168000, 300, 650),
  // Categories
  '2': mkStats(3, 12, 5000, 35000, 94000, 280, 920),
  '3': mkStats(1, 4, 1500, 10500, 28000, 267, 1050),
  '4': mkStats(0, 2, 800, 5600, 14000, 250, 1200),
  '5': mkStats(1, 3, 1200, 8400, 25200, 300, 880),
  '6': mkStats(0, 1, 500, 3500, 8750, 250, 1400),
  '7': mkStats(0, 0, 0, 0, 0, 0, 0),
  // Themes
  '8': mkStats(2, 8, 3000, 21000, 63000, 300, 850),
  '9': mkStats(1, 2, 1000, 7000, 17500, 250, 1100),
  '10': mkStats(0, 2, 1000, 7000, 14000, 200, 1050),
  '11': mkStats(1, 3, 1000, 7000, 21000, 300, 950),
  '12': mkStats(0, 1, 500, 3500, 7000, 200, 1200),
  '13': mkStats(0, 1, 400, 2800, 7000, 250, 1300),
  '14': mkStats(0, 1, 400, 2800, 7000, 250, 1100),
  '15': mkStats(1, 2, 800, 5600, 16800, 300, 900),
  '16': mkStats(0, 1, 400, 2800, 8400, 300, 850),
  '17': mkStats(0, 0, 0, 0, 0, 0, 0),
  '18': mkStats(0, 1, 500, 3500, 8750, 250, 1400),
  '19': mkStats(0, 0, 0, 0, 0, 0, 0),
  '20': mkStats(0, 0, 0, 0, 0, 0, 0),
  '21': mkStats(0, 0, 0, 0, 0, 0, 0),
  '22': mkStats(0, 0, 0, 0, 0, 0, 0),
  // Units
  '23': mkStats(1, 4, 1500, 10500, 31500, 300, 800),
  '24': mkStats(1, 2, 800, 5600, 14000, 250, 950),
  '25': mkStats(0, 2, 700, 4900, 14700, 300, 1000),
  '26': mkStats(1, 2, 1000, 7000, 17500, 250, 1100),
  '27': mkStats(0, 1, 500, 3500, 7000, 200, 1100),
  '28': mkStats(0, 1, 500, 3500, 7000, 200, 1000),
  '29': mkStats(1, 3, 1000, 7000, 21000, 300, 950),
  '30': mkStats(0, 0, 0, 0, 0, 0, 0),
  '31': mkStats(0, 1, 500, 3500, 7000, 200, 1200),
  '32': mkStats(0, 1, 400, 2800, 7000, 250, 1300),
  '33': mkStats(0, 1, 400, 2800, 7000, 250, 1100),
  '34': mkStats(1, 1, 500, 3500, 10500, 300, 900),
  '35': mkStats(0, 1, 300, 2100, 6300, 300, 900),
  '36': mkStats(0, 1, 400, 2800, 8400, 300, 850),
  '37': mkStats(0, 0, 0, 0, 0, 0, 0),
  '38': mkStats(0, 1, 500, 3500, 8750, 250, 1400),
  '39': mkStats(0, 0, 0, 0, 0, 0, 0),
  '40': mkStats(0, 0, 0, 0, 0, 0, 0),
  '41': mkStats(0, 0, 0, 0, 0, 0, 0),
  '42': mkStats(0, 0, 0, 0, 0, 0, 0),
  '43': mkStats(0, 0, 0, 0, 0, 0, 0),
  '44': mkStats(0, 0, 0, 0, 0, 0, 0),
  '45': mkStats(0, 0, 0, 0, 0, 0, 0),
  // Angles
  '46': mkStats(1, 3, 1200, 8400, 25200, 300, 780),
  '47': mkStats(0, 1, 500, 3500, 10500, 300, 850),
  '48': mkStats(0, 0, 0, 0, 0, 0, 0),
  '49': mkStats(1, 1, 400, 2800, 7000, 250, 950),
  '50': mkStats(0, 1, 400, 2800, 7000, 250, 1000),
  '51': mkStats(0, 1, 350, 2450, 7350, 300, 1050),
  '52': mkStats(0, 1, 350, 2450, 7350, 300, 950),
  '53': mkStats(1, 3, 1000, 7000, 21000, 300, 950),
  '54': mkStats(0, 1, 500, 3500, 8750, 250, 1400),
  '55': mkStats(0, 0, 0, 0, 0, 0, 0),
  // Offers
  '56': mkStats(3, 14, 5000, 35000, 105000, 300, 800),
  '57': mkStats(2, 8, 3000, 21000, 63000, 300, 950),
  // Cabinets
  '58': mkStats(2, 10, 4000, 28000, 84000, 300, 820),
  '59': mkStats(1, 4, 1500, 10500, 31500, 300, 900),
  '60': mkStats(0, 0, 0, 0, 0, 0, 0),
  '61': mkStats(1, 4, 1500, 10500, 28000, 267, 1050),
  '62': mkStats(0, 0, 0, 0, 0, 0, 0),
};

const formatMoney = (value: number): string => {
  if (value === 0) return '0 ₽';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M ₽`;
  if (value >= 1000) return `${Math.round(value / 1000).toLocaleString('ru-RU')} 000 ₽`;
  return `${value.toLocaleString('ru-RU')} ₽`;
};

const formatChange = (value: number): React.ReactNode => {
  if (value === 0) return null;
  const isPositive = value > 0;
  return (
    <span className={`sm-stat-change ${isPositive ? 'sm-stat-change--positive' : 'sm-stat-change--negative'}`}>
      {isPositive ? '+' : ''}{value}%
    </span>
  );
};

const PLATFORM_CONFIG: Record<AdPlatform, { label: string; icon: React.ReactNode; color: string }> = {
  yandex_direct: { label: 'Яндекс Директ', icon: <DesktopOutlined />, color: '#fc0' },
  vk: { label: 'ВКонтакте', icon: <DesktopOutlined />, color: '#4a76a8' },
};

export const mockAdsData: Ad[] = [
  // ang1 (Боль) для u1 (Похудеть к лету)
  { id: '63', angleId: '46', offerId: '56', platform: 'yandex_direct', cabinetId: '58', title: 'До лета 3 месяца, а вы всё ещё +15 кг?', title2: 'Бесплатный вебинар', text: 'Узнайте как начать худеть без диет и срывов. Вебинар-практикум.', url: 'example.com/webinar', displayUrl: 'example.com/похудение', sitelinks: [{ title: 'Программа', description: '', url: '#' }, { title: 'Отзывы', description: '', url: '#' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: ['Бесплатно', 'Без диет', 'Практика'], status: 'active', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
  { id: '64', angleId: '46', offerId: '56', platform: 'yandex_direct', cabinetId: '58', title: 'Каждый год обещаете похудеть к лету?', title2: 'В этот раз получится', text: 'Бесплатный вебинар: система питания, которая работает без срывов.', url: 'example.com/webinar', displayUrl: 'example.com/старт', sitelinks: [{ title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: ['Бесплатно', 'Онлайн'], status: 'draft', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
  // ang1 (Боль) для u1 + VK
  { id: '65', angleId: '46', offerId: '56', platform: 'vk', cabinetId: '61', title: 'До лета 3 месяца — пора действовать', title2: '', text: 'Бесплатный вебинар по нутрициологии. Как похудеть без диет и голодовок. Регистрируйтесь!', url: 'example.com/webinar', displayUrl: '', sitelinks: [{ title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: [], status: 'active', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
  // ang2 (FOMO) для u1
  { id: '66', angleId: '47', offerId: '56', platform: 'yandex_direct', cabinetId: '58', title: '127 участниц уже худеют к лету', title2: 'Присоединяйтесь', text: 'Набор на бесплатный вебинар закрывается. Не откладывайте.', url: 'example.com/webinar', displayUrl: 'example.com/места', sitelinks: [{ title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: ['Осталось 12 мест', 'Бесплатно'], status: 'active', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
  // ang6 (Провокация) для u3 (Перепробовала все диеты)
  { id: '67', angleId: '51', offerId: '57', platform: 'yandex_direct', cabinetId: '58', title: 'Диеты не работают. Хватит мучить себя', title2: 'Есть другой путь', text: 'Курс по нутрициологии: система питания без подсчёта калорий. Скидка 40%.', url: 'example.com/course', displayUrl: 'example.com/без-диет', sitelinks: [{ title: 'Программа', description: '', url: '#' }, { title: 'Стоимость', description: '', url: '#' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: ['Без подсчёта калорий', 'Скидка 40%', 'Рассрочка'], status: 'active', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
  // ang7 (Соц.доказательство) для u3
  { id: '68', angleId: '52', offerId: '56', platform: 'yandex_direct', cabinetId: '58', title: '89% участниц перестали сидеть на диетах', title2: 'И похудели', text: 'Бесплатный вебинар: кейсы женщин, которые нашли свою систему питания.', url: 'example.com/webinar', displayUrl: 'example.com/кейсы', sitelinks: [{ title: 'Кейсы', description: '', url: '#' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: ['500+ отзывов', 'Реальные истории'], status: 'active', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
  // ang9 (Боль) для u16 (Заедаю стресс)
  { id: '69', angleId: '54', offerId: '56', platform: 'yandex_direct', cabinetId: '58', title: 'Заедаете стресс? Это не ваша вина', title2: 'Узнайте причину', text: 'Бесплатный вебинар: почему вы переедаете и как остановиться без силы воли.', url: 'example.com/webinar', displayUrl: 'example.com/стресс', sitelinks: [{ title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: ['Бесплатно', 'Без силы воли'], status: 'active', actionButton: 'Перейти', imageText: '', imageButtonText: '' },
];

// ============================================================
// TYPES
// ============================================================

type ModalMode =
  | { type: 'sphere'; editing: LifeSphere | null }
  | { type: 'theme'; sphereId: string; editing: Theme | null }
  | { type: 'subtheme'; themeId: string; editing: Subtheme | null }
  | { type: 'unit'; subthemeId: string; editing: SemanticUnit | null };

type Level = 'spheres' | 'themes' | 'subthemes' | 'units' | 'angles' | 'offers' | 'platforms' | 'cabinets' | 'ads';

interface SemanticMapTabProps {
  productId: string;
}

// Autoincrement counter — next ID after all mock data
let nextEntityId = 70;
const getNextId = (): string => String(nextEntityId++);

// ============================================================
// CABINET PICKER MODAL
// ============================================================

// --- Editable cell with char counter (must be outside main component to avoid re-mount) ---

const EditCell: React.FC<{ value: string; max: number; onChange: (v: string) => void; multiline?: boolean; placeholder?: string }> = ({ value, max, onChange, placeholder }) => {
  const len = value.length;
  const over = len > max;
  return (
    <div className="ads-edit-cell">
      <Input.TextArea
        autoSize={{ minRows: 1 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ fontSize: 12 }}
      />
      <span className={`ads-edit-cell__count${over ? ' ads-edit-cell__count--over' : ''}`}>{len}/{max}</span>
    </div>
  );
};

const CabinetPickerModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onAdd: (ids: string[]) => void;
  cabinets: AdCabinet[];
  platformLabel: string;
}> = ({ visible, onClose, onAdd, cabinets, platformLabel }) => {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const toggle = (id: string) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const handleOk = () => { onAdd(Array.from(selected)); setSelected(new Set()); };
  const handleCancel = () => { onClose(); setSelected(new Set()); };

  return (
    <Modal title={`Добавить кабинет — ${platformLabel}`} visible={visible} onOk={handleOk} onCancel={handleCancel} okText={`Добавить (${selected.size})`} okButtonProps={{ disabled: selected.size === 0 }} cancelText="Отмена" width={480} destroyOnClose>
      {cabinets.length === 0 ? (
        <Empty description="Нет доступных кабинетов" style={{ padding: '24px 0' }} />
      ) : (
        <div className="cabinet-picker__list">
          {cabinets.map((cab) => (
            <div key={cab.id} className={`cabinet-picker__item${selected.has(cab.id) ? ' cabinet-picker__item--selected' : ''}`} onClick={() => toggle(cab.id)}>
              <Checkbox checked={selected.has(cab.id)} />
              <div className="cabinet-picker__info">
                <Text strong style={{ fontSize: 13 }}>{cab.name}</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>ID: {cab.externalId}</Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const SemanticMapTab: React.FC<SemanticMapTabProps> = ({ productId }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Data state ---
  const db = useDb();
  const spheres = useMemo(() => ds.getSpheresNested(productId), [db, productId]);
  const setSpheres: React.Dispatch<React.SetStateAction<LifeSphere[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: LifeSphere[]) => LifeSphere[])(spheres) : u;
    ds.replaceSpheresNested(productId, next);
  };
  const angles = db.angles;
  const setAngles: React.Dispatch<React.SetStateAction<Angle[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: Angle[]) => Angle[])(angles) : u;
    ds.setAngles(next);
  };
  const ads = db.ads;
  const setAds: React.Dispatch<React.SetStateAction<Ad[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: Ad[]) => Ad[])(ads) : u;
    ds.setAds(next);
  };
  const productCabinetIds = ds.listProductCabinetIds(productId);
  const setProductCabinetIds: React.Dispatch<React.SetStateAction<string[]>> = (u) => {
    const next = typeof u === 'function' ? (u as (p: string[]) => string[])(productCabinetIds) : u;
    ds.setProductCabinetIds(productId, next);
  };

  // --- UI state ---
  const [smViewMode, setSmViewMode] = useState<'cards' | 'table'>('cards');
  const [modalMode, setModalMode] = useState<ModalMode | null>(null);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [autoModeOpen, setAutoModeOpen] = useState(false);
  const [cabinetPickerVisible, setCabinetPickerVisible] = useState(false);
  const [platformSettingsOpen, setPlatformSettingsOpen] = useState<AdPlatform | null>(null);
  const [previewUnitId, setPreviewUnitId] = useState<string | null>(null);
  const [keywordsViewKey, setKeywordsViewKey] = useState<{ type: 'subtheme' | 'unit'; id: string } | null>(null);
  const [yaSettings, setYaSettings] = useState<YandexDirectGenerationSettings>(getDefaultYandexDirectSettings());
  const [editingImageAdId, setEditingImageAdId] = useState<string | null>(null);
  const [form] = Form.useForm();

  // --- Pinned ---
  const [pinnedOffer, setPinnedOffer] = useState<string | null>(null);
  const [pinnedPlatform, setPinnedPlatform] = useState<AdPlatform | null>(null);
  const [pinnedCabinet, setPinnedCabinet] = useState<string | null>(null);

  // --- URL params ---
  const p = (key: string) => searchParams.get(key) || null;
  const selectedSphereId = p('sphere');
  const selectedThemeId = p('theme');
  const selectedSubthemeId = p('subtheme');
  const selectedUnitId = p('unit');
  const selectedAngleId = p('angle');
  const selectedOfferId = p('offer');
  const selectedPlatform = p('platform') as AdPlatform | null;
  const selectedCabinetId = p('cabinet');

  const allKeys = ['sphere', 'theme', 'subtheme', 'unit', 'angle', 'offer', 'platform', 'cabinet'];

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    allKeys.forEach((key) => {
      if (key in updates) {
        updates[key] ? params.set(key, updates[key]!) : params.delete(key);
      }
    });
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const resetFrom = (level: Level): Record<string, null> => {
    const order: Level[] = ['spheres', 'themes', 'subthemes', 'units', 'offers', 'platforms', 'cabinets', 'angles', 'ads'];
    const keyMap: Record<Level, string> = { spheres: 'sphere', themes: 'theme', subthemes: 'subtheme', units: 'unit', angles: 'angle', offers: 'offer', platforms: 'platform', cabinets: 'cabinet', ads: '' };
    const idx = order.indexOf(level);
    const result: Record<string, null> = {};
    order.slice(idx).forEach((l) => { if (keyMap[l]) result[keyMap[l]] = null; });
    return result;
  };

  // --- Derived state ---
  const selectedSphere = useMemo(() => spheres.find((s) => s.id === selectedSphereId) || null, [spheres, selectedSphereId]);
  const selectedTheme = useMemo(() => selectedSphere?.themes.find((c) => c.id === selectedThemeId) || null, [selectedSphere, selectedThemeId]);
  const selectedSubtheme = useMemo(() => selectedTheme?.subthemes.find((t) => t.id === selectedSubthemeId) || null, [selectedTheme, selectedSubthemeId]);
  const selectedUnit = useMemo(() => selectedSubtheme?.units.find((u) => u.id === selectedUnitId) || null, [selectedSubtheme, selectedUnitId]);
  const offers = useMemo<SmOffer[]>(() => db.offers.filter((o) => o.productId === productId) as SmOffer[], [db.offers, productId]);
  const selectedOffer = useMemo(() => offers.find((o) => o.id === selectedOfferId) || null, [offers, selectedOfferId]);
  const cabinets = useMemo(() => db.adCabinets.filter((c) => productCabinetIds.includes(c.id) && c.platform === selectedPlatform), [db.adCabinets, productCabinetIds, selectedPlatform]);
  const selectedCabinet = useMemo(() => cabinets.find((c) => c.id === selectedCabinetId) || null, [cabinets, selectedCabinetId]);
  const currentAngles = useMemo(() => selectedUnitId ? angles.filter((a) => a.unitId === selectedUnitId) : [], [angles, selectedUnitId]);
  const selectedAngle = useMemo(() => angles.find((a) => a.id === selectedAngleId) || null, [angles, selectedAngleId]);
  const currentAds = useMemo(() => {
    if (!selectedAngleId || !selectedOfferId || !selectedPlatform || !selectedCabinetId) return [];
    return ads.filter((a) => a.angleId === selectedAngleId && a.offerId === selectedOfferId && a.platform === selectedPlatform && a.cabinetId === selectedCabinetId);
  }, [ads, selectedAngleId, selectedOfferId, selectedPlatform, selectedCabinetId]);

  // --- Auto-create angles for selected unit (on URL restore / page reload) ---
  React.useEffect(() => {
    if (!selectedUnitId) return;
    const existing = angles.filter((a) => a.unitId === selectedUnitId);
    if (existing.length === 0) {
      const allTypes = Object.keys(ANGLE_TYPE_CONFIG) as AngleType[];
      let counter = Date.now();
      const newAngles = allTypes.map((type) => ({ id: String(counter++), unitId: selectedUnitId, type, enabled: true, score: 0, reasoning: '' }));
      setAngles((prev) => [...prev, ...newAngles]);
    }
  }, [selectedUnitId]);

  // --- Current level ---
  // Order: spheres → categories → themes → units → offers → platforms → cabinets → angles → ads
  const currentLevel: Level = selectedAngleId ? 'ads'
    : selectedCabinetId ? 'angles'
    : selectedPlatform ? 'cabinets'
    : selectedOfferId ? 'platforms'
    : selectedUnitId ? 'offers'
    : selectedSubthemeId ? 'units'
    : selectedThemeId ? 'subthemes'
    : selectedSphereId ? 'themes'
    : 'spheres';

  // --- Navigation ---
  const handleSelectSphere = (id: string) => updateParams({ sphere: id, ...resetFrom('themes') });
  const handleSelectTheme = (id: string) => updateParams({ theme: id, ...resetFrom('subthemes') });
  const handleSelectSubtheme = (id: string) => updateParams({ subtheme: id, ...resetFrom('units') });
  const handleSelectUnit = (id: string) => {
    // Auto-create all angle types for this unit if none exist
    const existing = angles.filter((a) => a.unitId === id);
    if (existing.length === 0) {
      const allTypes = Object.keys(ANGLE_TYPE_CONFIG) as AngleType[];
      let counter = Date.now();
      const newAngles = allTypes.map((type) => ({ id: String(counter++), unitId: id, type, enabled: true, score: 0, reasoning: '' }));
      setAngles((prev) => [...prev, ...newAngles]);
    }
    // Apply pins: offers → platforms → cabinets
    if (pinnedOffer && pinnedPlatform && pinnedCabinet) {
      updateParams({ unit: id, offer: pinnedOffer, platform: pinnedPlatform, cabinet: pinnedCabinet, angle: null });
    } else if (pinnedOffer && pinnedPlatform) {
      updateParams({ unit: id, offer: pinnedOffer, platform: pinnedPlatform, cabinet: null, angle: null });
    } else if (pinnedOffer) {
      updateParams({ unit: id, offer: pinnedOffer, platform: null, cabinet: null, angle: null });
    } else {
      updateParams({ unit: id, ...resetFrom('offers') });
    }
  };

  const handleSelectOffer = (id: string) => {
    if (pinnedPlatform && pinnedCabinet) {
      updateParams({ offer: id, platform: pinnedPlatform, cabinet: pinnedCabinet, angle: null });
    } else if (pinnedPlatform) {
      updateParams({ offer: id, platform: pinnedPlatform, cabinet: null, angle: null });
    } else {
      updateParams({ offer: id, platform: null, cabinet: null, angle: null });
    }
  };

  const handleSelectPlatform = (platform: AdPlatform) => {
    if (pinnedCabinet) {
      const cab = allConnectedCabinets.find((c) => c.id === pinnedCabinet && c.platform === platform);
      if (cab) { updateParams({ platform, cabinet: pinnedCabinet, angle: null }); return; }
    }
    updateParams({ platform, cabinet: null, angle: null });
  };

  const handleSelectCabinet = (id: string) => updateParams({ cabinet: id, angle: null });

  const handleSelectAngle = (id: string) => updateParams({ angle: id });

  const handleClickTab = (level: Level) => {
    if (level === 'offers' && !selectedUnitId) {
      updateParams(resetFrom('offers'));
    } else if (level === 'platforms' && !selectedOfferId && pinnedOffer) {
      updateParams({ offer: pinnedOffer, platform: null, cabinet: null, angle: null });
    } else if (level === 'cabinets' && !selectedPlatform && pinnedPlatform) {
      updateParams({ offer: selectedOfferId || pinnedOffer, platform: pinnedPlatform, cabinet: null, angle: null });
    } else if (level === 'angles' && !selectedCabinetId && pinnedCabinet) {
      updateParams({ offer: selectedOfferId || pinnedOffer, platform: selectedPlatform || pinnedPlatform, cabinet: pinnedCabinet, angle: null });
    } else {
      updateParams(resetFrom(level));
    }
  };

  // --- Pin handlers ---
  const handleTogglePinOffer = (id: string) => setPinnedOffer((prev) => prev === id ? null : id);
  const handleTogglePinPlatform = (pl: AdPlatform) => setPinnedPlatform((prev) => prev === pl ? null : pl);
  const handleTogglePinCabinet = (id: string) => setPinnedCabinet((prev) => prev === id ? null : id);

  // --- Angle CRUD ---
  const handleDeleteAngle = (id: string) => {
    setAngles((prev) => prev.filter((a) => a.id !== id));
    if (selectedAngleId === id) updateParams(resetFrom('angles'));
    message.success('Угол удалён');
  };

  // --- Cabinet CRUD ---
  const availableCabinetsForPicker = useMemo(() => {
    if (!selectedPlatform) return [];
    return allConnectedCabinets.filter((c) => c.platform === selectedPlatform && !productCabinetIds.includes(c.id));
  }, [selectedPlatform, productCabinetIds]);

  const handleAddCabinets = (ids: string[]) => {
    setProductCabinetIds((prev) => [...prev, ...ids]);
    message.success(`Добавлено ${ids.length} кабинетов`);
    setCabinetPickerVisible(false);
  };

  const handleRemoveCabinet = (id: string) => {
    setProductCabinetIds((prev) => prev.filter((cid) => cid !== id));
    if (selectedCabinetId === id) updateParams({ cabinet: null });
    if (pinnedCabinet === id) setPinnedCabinet(null);
    message.success('Кабинет убран');
  };

  // --- Sphere/Category/Theme/Unit CRUD ---
  const handleAdd = () => {
    form.resetFields();
    if (currentLevel === 'spheres') setModalMode({ type: 'sphere', editing: null });
    else if (currentLevel === 'themes' && selectedSphereId) setModalMode({ type: 'theme', sphereId: selectedSphereId, editing: null });
    else if (currentLevel === 'subthemes' && selectedThemeId) setModalMode({ type: 'subtheme', themeId: selectedThemeId, editing: null });
    else if (currentLevel === 'units' && selectedSubthemeId) setModalMode({ type: 'unit', subthemeId: selectedSubthemeId, editing: null });
  };

  const handleEdit = (entity: LifeSphere | Theme | Subtheme | SemanticUnit, type: ModalMode['type']) => {
    const common = { status: (entity as { status?: NodeStatus }).status ?? 'draft', priority: (entity as { priority?: number }).priority ?? 5 };
    if (type === 'sphere') {
      const e = entity as LifeSphere;
      setModalMode({ type: 'sphere', editing: e });
      form.setFieldsValue({ name: e.name, description: e.description, ...common });
    } else if (type === 'theme') {
      const e = entity as Theme;
      setModalMode({ type: 'theme', sphereId: selectedSphereId!, editing: e });
      form.setFieldsValue({ name: e.name, description: e.description, images: (e.images || []).join('\n'), ...common });
    } else if (type === 'subtheme') {
      const e = entity as Subtheme;
      setModalMode({ type: 'subtheme', themeId: selectedThemeId!, editing: e });
      form.setFieldsValue({ name: e.name, description: e.description, keywords: (e.keywords || []).join('\n'), negativeKeywords: (e.negativeKeywords || []).join('\n'), ...common });
    } else if (type === 'unit') {
      const e = entity as SemanticUnit;
      setModalMode({ type: 'unit', subthemeId: selectedSubthemeId!, editing: e });
      form.setFieldsValue({ name: e.name, description: e.description, examples: e.examples.join('\n'), keywords: (e.keywords || []).join('\n'), negativeKeywords: (e.negativeKeywords || []).join('\n'), ...common });
    }
  };

  const handleDelete = (id: string, type: ModalMode['type']) => {
    if (type === 'sphere') {
      setSpheres((prev) => prev.filter((s) => s.id !== id));
      if (selectedSphereId === id) updateParams(resetFrom('spheres'));
    } else if (type === 'theme') {
      setSpheres((prev) => prev.map((s) => s.id === selectedSphereId ? { ...s, themes: s.themes.filter((c) => c.id !== id) } : s));
      if (selectedThemeId === id) updateParams(resetFrom('themes'));
    } else if (type === 'subtheme') {
      setSpheres((prev) => prev.map((s) => ({ ...s, themes: s.themes.map((c) => c.id === selectedThemeId ? { ...c, subthemes: c.subthemes.filter((t) => t.id !== id) } : c) })));
      if (selectedSubthemeId === id) updateParams(resetFrom('subthemes'));
    } else if (type === 'unit') {
      setSpheres((prev) => prev.map((s) => ({ ...s, themes: s.themes.map((c) => ({ ...c, subthemes: c.subthemes.map((t) => t.id === selectedSubthemeId ? { ...t, units: t.units.filter((u) => u.id !== id) } : t) })) })));
      if (selectedUnitId === id) updateParams(resetFrom('units'));
    }
    message.success('Удалено');
  };

  const handleSubmit = () => {
    if (!modalMode) return;
    form.validateFields().then((values) => {
      const m = modalMode;
      const status: NodeStatus = (values.status as NodeStatus) || 'draft';
      const priority: number = typeof values.priority === 'number' ? values.priority : 5;
      if (m.type === 'sphere') {
        if (m.editing) {
          setSpheres((prev) => prev.map((s) => s.id === m.editing!.id ? { ...s, name: values.name, description: values.description || '', status, priority } : s));
        } else {
          setSpheres((prev) => [...prev, { id: getNextId(), productId, name: values.name, description: values.description || '', themes: [], status, priority }]);
        }
      } else if (m.type === 'theme') {
        const images = (values.images || '').split('\n').map((e: string) => e.trim()).filter(Boolean);
        const newTheme: Theme = { id: getNextId(), sphereId: m.sphereId, name: values.name, description: values.description || '', subthemes: [], images, status, priority };
        if (m.editing) {
          setSpheres((prev) => prev.map((s) => s.id === m.sphereId ? { ...s, themes: s.themes.map((c) => c.id === m.editing!.id ? { ...c, name: values.name, description: values.description || '', images, status, priority } : c) } : s));
        } else {
          setSpheres((prev) => prev.map((s) => s.id === m.sphereId ? { ...s, themes: [...s.themes, newTheme] } : s));
        }
      } else if (m.type === 'subtheme') {
        const keywords = (values.keywords || '').split('\n').map((e: string) => e.trim()).filter(Boolean);
        const negativeKeywords = (values.negativeKeywords || '').split('\n').map((e: string) => e.trim()).filter(Boolean);
        const newSubtheme: Subtheme = { id: getNextId(), themeId: m.themeId, name: values.name, description: values.description || '', units: [], keywords, negativeKeywords, status, priority };
        if (m.editing) {
          setSpheres((prev) => prev.map((s) => ({ ...s, themes: s.themes.map((c) => c.id === m.themeId ? { ...c, subthemes: c.subthemes.map((t) => t.id === m.editing!.id ? { ...t, name: values.name, description: values.description || '', keywords, negativeKeywords, status, priority } : t) } : c) })));
        } else {
          setSpheres((prev) => prev.map((s) => ({ ...s, themes: s.themes.map((c) => c.id === m.themeId ? { ...c, subthemes: [...c.subthemes, newSubtheme] } : c) })));
        }
      } else if (m.type === 'unit') {
        const examples = (values.examples || '').split('\n').map((e: string) => e.trim()).filter(Boolean);
        const keywords = (values.keywords || '').split('\n').map((e: string) => e.trim()).filter(Boolean);
        const negativeKeywords = (values.negativeKeywords || '').split('\n').map((e: string) => e.trim()).filter(Boolean);
        const newUnit: SemanticUnit = { id: getNextId(), subthemeId: m.subthemeId, name: values.name, description: values.description || '', examples, keywords, negativeKeywords, status, priority };
        if (m.editing) {
          setSpheres((prev) => prev.map((s) => ({ ...s, themes: s.themes.map((c) => ({ ...c, subthemes: c.subthemes.map((t) => t.id === m.subthemeId ? { ...t, units: t.units.map((u) => u.id === m.editing!.id ? { ...u, name: values.name, description: values.description || '', examples, keywords, negativeKeywords, status, priority } : u) } : t) })) })));
        } else {
          setSpheres((prev) => prev.map((s) => ({ ...s, themes: s.themes.map((c) => ({ ...c, subthemes: c.subthemes.map((t) => t.id === m.subthemeId ? { ...t, units: [...t.units, newUnit] } : t) })) })));
        }
      }
      message.success(m.editing ? 'Обновлено' : 'Создано');
      setModalMode(null);
      form.resetFields();
    });
  };

  // --- Inline ad generation ---
  const handleInlineGenerate = (count: number) => {
    if (!selectedAngle || !selectedOffer || !selectedPlatform || !selectedCabinet || !selectedUnit) return;
    const tpls = [
      { title: 'Вариант для «{u}»', title2: 'Узнайте больше', text: '{o} — решение «{u}».' },
      { title: 'Решите «{u}» навсегда', title2: 'Начните сегодня', text: 'Присоединяйтесь к {o}.' },
      { title: 'Новый подход к «{u}»', title2: 'Попробуйте', text: '{o} — конкретный план по «{u}».' },
    ];
    const newAds: Ad[] = [];
    for (let i = 0; i < count; i++) {
      const t = tpls[i % tpls.length];
      const f = (s: string) => s.replace(/\{u\}/g, selectedUnit.name.toLowerCase()).replace(/\{o\}/g, selectedOffer.name);
      newAds.push({ id: getNextId(), angleId: selectedAngle.id, offerId: selectedOffer.id, platform: selectedPlatform, cabinetId: selectedCabinet.id, title: f(t.title), title2: selectedPlatform === 'yandex_direct' ? f(t.title2) : '', text: f(t.text), url: 'example.com', displayUrl: selectedPlatform === 'yandex_direct' ? 'example.com' : '', sitelinks: [{ title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }, { title: '', description: '', url: '' }], callouts: [], status: 'draft', actionButton: 'Перейти', imageText: '', imageButtonText: '' });
    }
    setAds((prev) => [...prev, ...newAds]);
    message.success(`Сгенерировано ${count} объявлений`);
  };

  // --- Count helpers ---
  const countAngles = (unitId: string) => angles.filter((a) => a.unitId === unitId).length;
  const countAdsAngle = (angleId: string) => ads.filter((a) => a.angleId === angleId).length;
  const countAdsOffer = (angleId: string, offerId: string) => ads.filter((a) => a.angleId === angleId && a.offerId === offerId).length;
  const countAdsPlatform = (angleId: string, offerId: string, pl: AdPlatform) => ads.filter((a) => a.angleId === angleId && a.offerId === offerId && a.platform === pl).length;
  const countAdsCabinet = (angleId: string, offerId: string, pl: AdPlatform, cabId: string) => ads.filter((a) => a.angleId === angleId && a.offerId === offerId && a.platform === pl && a.cabinetId === cabId).length;

  // --- Generic card renderer ---
  const renderStatusBadge = (status?: NodeStatus, priority?: number) => {
    const parts: React.ReactNode[] = [];
    if (status) {
      const cfg = NODE_STATUS_CONFIG[status];
      parts.push(<Tag key="s" color={cfg.color} style={{ fontSize: 10, padding: '0 6px', lineHeight: '16px', marginRight: 4 }}>{cfg.label}</Tag>);
    }
    if (priority && priority > 0) {
      parts.push(<Tag key="p" style={{ fontSize: 10, padding: '0 6px', lineHeight: '16px', marginRight: 0 }}>★ {priority}</Tag>);
    }
    return parts.length ? <Space size={0}>{parts}</Space> : null;
  };

  const renderCard = (
    key: string, name: string, desc: string | null, meta: string,
    onClick: () => void,
    onEdit?: () => void, onDelete?: () => void,
    icon?: React.ReactNode, isPinned?: boolean, onPin?: () => void,
    extra?: React.ReactNode,
    iconStyle?: React.CSSProperties,
    num?: number,
    onPreview?: () => void,
    onShowKeywords?: () => void,
    nodeStatus?: NodeStatus,
    nodePriority?: number,
  ) => (
    <div key={key} className={`sm-card${isPinned ? ' sm-card--pinned' : ''}`} onClick={onClick}>
      <div className="sm-card__body">
        {icon && <div className="sm-card__icon" style={iconStyle}>{icon}</div>}
        <div className="sm-card__info">
          {(extra || nodeStatus || (nodePriority && nodePriority > 0)) && (
            <div style={{ marginBottom: 4, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              {renderStatusBadge(nodeStatus, nodePriority)}
              {extra}
            </div>
          )}
          <div className="sm-card__name-row">
            <Text strong className="sm-card__name">{name}</Text>
            {num != null && <Text type="secondary" className="sm-card__id">#{num}</Text>}
          </div>
          {desc && <Text type="secondary" className="sm-card__desc">{desc}</Text>}
        </div>
      </div>
      <div className="sm-card__meta">
        <Text type="secondary" style={{ fontSize: 12 }}>{meta}</Text>
        <Space size={4} onClick={(e) => e.stopPropagation()}>
          {onShowKeywords && (
            <Tooltip title="Ключевые и минус-слова"><Button type="text" size="small" icon={<UnorderedListOutlined />} onClick={onShowKeywords} style={{ color: '#8c8c8c' }} /></Tooltip>
          )}
          {onPreview && (
            <Tooltip title="Превью"><Button type="text" size="small" icon={<EyeOutlined />} onClick={onPreview} style={{ color: '#8c8c8c' }} /></Tooltip>
          )}
          {onEdit && <Tooltip title="Редактировать"><Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} /></Tooltip>}
          {onDelete && (
            <Popconfirm title="Удалить?" onConfirm={onDelete} okText="Да" cancelText="Нет">
              <Tooltip title="Удалить"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
            </Popconfirm>
          )}
          {onPin && (
            <Tooltip title={isPinned ? 'Открепить' : 'Закрепить'}>
              <Button type="text" size="small" className={`sm-card__pin${isPinned ? ' sm-card__pin--active' : ''}`} icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />} onClick={onPin} />
            </Tooltip>
          )}
        </Space>
      </div>
    </div>
  );

  // --- Stats cell renderer ---
  const renderStatCell = (val: number, change: number, isRomi: boolean, isMoney: boolean) => (
    <div>
      <div style={{ fontWeight: 600, color: isRomi && val > 0 ? '#52c41a' : '#262626' }}>
        {val === 0 ? '—' : isRomi ? `+${val}%` : isMoney ? formatMoney(val) : String(val)}
      </div>
      {change !== 0 && formatChange(change)}
    </div>
  );

  // --- Stats columns for table ---
  const makeStatsColumns = <T extends { id: string }>(
    nameRender: (record: T) => React.ReactNode,
    onRowClick: (record: T) => void,
    extraColumns?: ColumnsType<T>,
  ): ColumnsType<T> => {
    const periodKeys = ['7d', '14d', '30d', 'all'] as const;
    const metrics: Array<{ title: string; key: keyof EntityPeriodStats; isMoney: boolean; isRomi: boolean }> = [
      { title: 'Расход', key: 'spend', isMoney: true, isRomi: false },
      { title: 'Прибыль', key: 'profit', isMoney: true, isRomi: false },
      { title: 'ROMI', key: 'romi', isMoney: false, isRomi: true },
      { title: 'CPL', key: 'cpl', isMoney: true, isRomi: false },
    ];

    const nameCol: ColumnsType<T> = [
      {
        title: '#',
        key: 'num',
        width: 50,
        fixed: 'left' as const,
        render: (_: unknown, __: T, index: number) => <Text type="secondary" style={{ fontSize: 12 }}>#{index + 1}</Text>,
      },
      {
        title: 'Название',
        key: 'name',
        fixed: 'left' as const,
        width: 220,
        render: (_: unknown, record: T) => (
          <a onClick={(e) => { e.preventDefault(); onRowClick(record); }} style={{ cursor: 'pointer', color: '#1890ff', fontWeight: 500 }}>
            {nameRender(record)}
          </a>
        ),
      },
    ];

    const statCols: ColumnsType<T> = [];
    periodKeys.forEach((pk) => {
      metrics.forEach((m) => {
        statCols.push({
          title: <div style={{ textAlign: 'center', lineHeight: 1.2 }}><div style={{ fontSize: 11, color: '#8c8c8c' }}>{PERIOD_LABELS[pk]}</div><div style={{ fontSize: 12 }}>{m.title}</div></div>,
          key: `${pk}_${m.key}`,
          width: 110,
          align: 'right' as const,
          sorter: (a: T, b: T) => {
            const sa = mockEntityStats[a.id];
            const sb = mockEntityStats[b.id];
            return (sa?.periods[pk][m.key] || 0) - (sb?.periods[pk][m.key] || 0);
          },
          render: (_: unknown, record: T) => {
            const stats = mockEntityStats[record.id];
            if (!stats) return '—';
            const changeKey = (m.key + 'Change') as keyof EntityPeriodStats;
            return renderStatCell(stats.periods[pk][m.key], pk !== 'all' ? stats.periods[pk][changeKey] : 0, m.isRomi, m.isMoney);
          },
        });
      });
    });

    return [...nameCol, ...(extraColumns || []), ...statCols];
  };

  // --- Table row type wrappers ---
  interface TableEntity { id: string; name: string; description: string }

  // --- Table renderers ---
  const renderSpheresTable = () => {
    const data = spheres.map((s) => ({ ...s }));
    const cols = makeStatsColumns<LifeSphere>(
      (r) => r.name,
      (r) => handleSelectSphere(r.id),
      [{ title: 'Тем', key: 'cats', width: 90, align: 'center' as const, render: (_: unknown, r: LifeSphere) => r.themes.length }],
    );
    return <Table rowKey="id" columns={cols} dataSource={data} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет сфер' }} />;
  };

  const renderThemesTable = () => {
    if (!selectedSphere) return null;
    const data = selectedSphere.themes;
    const cols = makeStatsColumns<Theme>(
      (r) => r.name,
      (r) => handleSelectTheme(r.id),
      [{ title: 'Подтем', key: 'subthemes', width: 80, align: 'center' as const, render: (_: unknown, r: Theme) => r.subthemes.length }],
    );
    return <Table rowKey="id" columns={cols} dataSource={data} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет тем' }} />;
  };

  const renderSubthemesTable = () => {
    if (!selectedTheme) return null;
    const data = selectedTheme.subthemes;
    const cols = makeStatsColumns<Subtheme>(
      (r) => r.name,
      (r) => handleSelectSubtheme(r.id),
      [{ title: 'СЕ', key: 'units', width: 70, align: 'center' as const, render: (_: unknown, r: Subtheme) => r.units.length }],
    );
    return <Table rowKey="id" columns={cols} dataSource={data} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет подтем' }} />;
  };

  const renderUnitsTable = () => {
    if (!selectedSubtheme) return null;
    const data = selectedSubtheme.units;
    const cols = makeStatsColumns<SemanticUnit>(
      (r) => r.name,
      (r) => handleSelectUnit(r.id),
      [{ title: 'Углов', key: 'angles', width: 70, align: 'center' as const, render: (_: unknown, r: SemanticUnit) => countAngles(r.id) }],
    );
    return <Table rowKey="id" columns={cols} dataSource={data} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет смысловых единиц' }} />;
  };

  const renderAnglesTable = () => {
    if (!selectedUnit) return null;
    const data = currentAngles;
    const cols = makeStatsColumns<Angle>(
      (r) => <Tag>{ANGLE_TYPE_CONFIG[r.type].label}</Tag>,
      (r) => handleSelectAngle(r.id),
      [{ title: 'Объявл.', key: 'adscnt', width: 80, align: 'center' as const, render: (_: unknown, r: Angle) => countAdsAngle(r.id) }],
    );
    return <Table rowKey="id" columns={cols} dataSource={data} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет стилей' }} />;
  };

  const renderOffersTable = () => {
    if (!selectedUnit) return null;
    const data = offers;
    const cols = makeStatsColumns<SmOffer>(
      (r) => r.name,
      (r) => handleSelectOffer(r.id),
      [{ title: 'Объявл.', key: 'adscnt', width: 80, align: 'center' as const, render: (_: unknown, r: SmOffer) => ads.filter((a) => a.offerId === r.id).length }],
    );
    return <Table rowKey="id" columns={cols} dataSource={data} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет офферов' }} />;
  };

  const renderPlatformsTable = () => {
    if (!selectedOffer) return null;
    const platforms: Array<{ id: string; name: string; platform: AdPlatform }> = [
      { id: 'platform_yandex_direct', name: 'Яндекс Директ', platform: 'yandex_direct' },
      { id: 'platform_vk', name: 'ВКонтакте', platform: 'vk' },
    ];
    type PlRow = typeof platforms[number];
    const cols: ColumnsType<PlRow> = [
      { title: '#', key: 'num', width: 50, render: (_: unknown, __: PlRow, index: number) => <Text type="secondary" style={{ fontSize: 12 }}>#{index + 1}</Text> },
      { title: 'Площадка', key: 'name', width: 220, render: (_: unknown, r: PlRow) => <a onClick={() => handleSelectPlatform(r.platform)} style={{ cursor: 'pointer', color: '#1890ff', fontWeight: 500 }}>{r.name}</a> },
      { title: 'Объявл.', key: 'ads', width: 80, align: 'center' as const, render: (_: unknown, r: PlRow) => ads.filter((a) => a.offerId === selectedOffer!.id && a.platform === r.platform).length },
    ];
    // add stat columns using cab1/cab4 as proxy stats
    const periodKeys = ['7d', '14d', '30d', 'all'] as const;
    const metrics: Array<{ title: string; key: keyof EntityPeriodStats; isMoney: boolean; isRomi: boolean }> = [
      { title: 'Расход', key: 'spend', isMoney: true, isRomi: false },
      { title: 'Прибыль', key: 'profit', isMoney: true, isRomi: false },
      { title: 'ROMI', key: 'romi', isMoney: false, isRomi: true },
      { title: 'CPL', key: 'cpl', isMoney: true, isRomi: false },
    ];
    periodKeys.forEach((pk) => {
      metrics.forEach((m) => {
        cols.push({
          title: <div style={{ textAlign: 'center', lineHeight: 1.2 }}><div style={{ fontSize: 11, color: '#8c8c8c' }}>{PERIOD_LABELS[pk]}</div><div style={{ fontSize: 12 }}>{m.title}</div></div>,
          key: `${pk}_${m.key}`,
          width: 110,
          align: 'right' as const,
          render: (_: unknown, r: PlRow) => {
            const statsId = r.platform === 'yandex_direct' ? '58' : '61';
            const stats = mockEntityStats[statsId];
            if (!stats) return '—';
            const changeKey = (m.key + 'Change') as keyof EntityPeriodStats;
            return renderStatCell(stats.periods[pk][m.key], pk !== 'all' ? stats.periods[pk][changeKey] : 0, m.isRomi, m.isMoney);
          },
        });
      });
    });
    return <Table rowKey="id" columns={cols} dataSource={platforms} pagination={false} scroll={{ x: 1200 }} size="small" />;
  };

  const renderCabinetsTable = () => {
    if (!selectedPlatform) return null;
    const cols = makeStatsColumns<AdCabinet>(
      (r) => <span>{r.name} <span style={{ color: '#bfbfbf', fontSize: 11 }}>ID: {r.externalId}</span></span>,
      (r) => handleSelectCabinet(r.id),
      [{ title: 'Объявл.', key: 'adscnt', width: 80, align: 'center' as const, render: (_: unknown, r: AdCabinet) => ads.filter((a) => a.offerId === selectedOffer!.id && a.platform === selectedPlatform && a.cabinetId === r.id).length }],
    );
    return <Table rowKey="id" columns={cols} dataSource={cabinets} pagination={false} scroll={{ x: 1200 }} size="small" locale={{ emptyText: 'Нет кабинетов' }} />;
  };

  // --- Render levels ---

  const renderSpheres = () => spheres.length === 0
    ? <Empty description="Нет сфер жизни. Добавьте первую." style={{ padding: '48px 0' }} />
    : <div className="sm-cards">{spheres.map((s, i) => renderCard(s.id, s.name, s.description, `${s.themes.length} тем`, () => handleSelectSphere(s.id), () => handleEdit(s, 'sphere'), () => handleDelete(s.id, 'sphere'), <AimOutlined />, false, undefined, undefined, undefined, i + 1, undefined, undefined, s.status, s.priority))}</div>;

  const renderThemes = () => {
    if (!selectedSphere) return null;
    const themes = selectedSphere.themes;
    return themes.length === 0
      ? <Empty description="Нет тем. Добавьте первую." style={{ padding: '48px 0' }} />
      : <div className="sm-cards">{themes.map((c, i) => renderCard(c.id, c.name, c.description, `${c.subthemes.length} подтем`, () => handleSelectTheme(c.id), () => handleEdit(c, 'theme'), () => handleDelete(c.id, 'theme'), <AppstoreOutlined />, false, undefined, undefined, { background: '#f0f5ff', color: '#2f54eb' }, i + 1, undefined, undefined, c.status, c.priority))}</div>;
  };

  const renderSubthemes = () => {
    if (!selectedTheme) return null;
    const subthemes = selectedTheme.subthemes;
    return subthemes.length === 0
      ? <Empty description="Нет подтем. Добавьте первую." style={{ padding: '48px 0' }} />
      : <div className="sm-cards">{subthemes.map((t, i) => renderCard(t.id, t.name, t.description, `${t.units.length} смысл. единиц`, () => handleSelectSubtheme(t.id), () => handleEdit(t, 'subtheme'), () => handleDelete(t.id, 'subtheme'), undefined, false, undefined, undefined, undefined, i + 1, undefined, () => setKeywordsViewKey({ type: 'subtheme', id: t.id }), t.status, t.priority))}</div>;
  };

  // Mock: count campaigns using each SU (for variant 1: badge)
  const mockUnitCampaignCount: Record<string, number> = { '23': 2, '24': 1, '25': 2, '26': 1, '27': 1 };

  const renderUnits = () => {
    if (!selectedSubtheme) return null;
    const units = selectedSubtheme.units;
    return units.length === 0
      ? <Empty description="Нет смысловых единиц. Добавьте первую." style={{ padding: '48px 0' }} />
      : <div className="sm-cards">{units.map((u, i) => {
          const campCount = mockUnitCampaignCount[u.id] || 0;
          const metaParts: string[] = [];
          if (countAngles(u.id) > 0) metaParts.push(`${countAngles(u.id)} стилей`);
          if (campCount > 0) metaParts.push(`${campCount} камп.`);
          if (metaParts.length === 0) metaParts.push('Нет стилей');
          return renderCard(u.id, u.name, u.description, metaParts.join(' · '), () => handleSelectUnit(u.id), () => handleEdit(u, 'unit'), () => handleDelete(u.id, 'unit'), undefined, false, undefined, undefined, undefined, i + 1, () => setPreviewUnitId(u.id), () => setKeywordsViewKey({ type: 'unit', id: u.id }), u.status, u.priority);
        })}</div>;
  };

  const handleToggleAngle = (angleId: string) => {
    setAngles((prev) => prev.map((a) => a.id === angleId ? { ...a, enabled: !a.enabled } : a));
  };

  const handleUpdateAngle = (angleId: string, field: 'score' | 'reasoning', value: number | string) => {
    setAngles((prev) => prev.map((a) => a.id === angleId ? { ...a, [field]: value } : a));
  };

  const handleToggleAllAngles = (checked: boolean) => {
    setAngles((prev) => prev.map((a) => a.unitId === selectedUnitId ? { ...a, enabled: checked } : a));
  };

  const renderAngles = () => {
    if (!selectedUnit || !selectedCabinet) return null;
    const enabledCount = currentAngles.filter((a) => a.enabled).length;
    const allChecked = enabledCount === currentAngles.length;
    const indeterminate = enabledCount > 0 && enabledCount < currentAngles.length;

    const columns = [
      {
        title: <Checkbox checked={allChecked} indeterminate={indeterminate} onChange={(e) => handleToggleAllAngles(e.target.checked)} />,
        key: 'enabled',
        width: 40,
        render: (_: unknown, record: Angle) => (
          <Checkbox checked={record.enabled} onChange={() => handleToggleAngle(record.id)} />
        ),
      },
      {
        title: '#',
        key: 'num',
        width: 40,
        render: (_: unknown, __: Angle, index: number) => (
          <Text type="secondary" style={{ fontSize: 11 }}>{index + 1}</Text>
        ),
      },
      {
        title: 'Стиль',
        key: 'label',
        width: 170,
        render: (_: unknown, record: Angle) => (
          <a
            onClick={() => handleSelectAngle(record.id)}
            style={{ fontWeight: 500, color: '#1890ff', cursor: 'pointer' }}
          >
            {ANGLE_TYPE_CONFIG[record.type].label}
          </a>
        ),
      },
      {
        title: 'Описание',
        key: 'description',
        render: (_: unknown, record: Angle) => (
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'normal', lineHeight: '1.5' }}>
            {ANGLE_TYPE_CONFIG[record.type].description}
          </Text>
        ),
      },
      {
        title: 'Пример',
        key: 'example',
        width: 280,
        render: (_: unknown, record: Angle) => (
          <Text italic style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'normal', lineHeight: '1.5' }}>
            «{ANGLE_TYPE_CONFIG[record.type].example}»
          </Text>
        ),
      },
      {
        title: 'Оценка',
        key: 'score',
        width: 70,
        align: 'center' as const,
        sorter: (a: Angle, b: Angle) => a.score - b.score,
        render: (_: unknown, record: Angle) => {
          const s = record.score;
          const color = s >= 80 ? '#52c41a' : s >= 50 ? '#faad14' : s > 0 ? '#ff4d4f' : '#d9d9d9';
          return <Text strong style={{ fontSize: 13, color }}>{s > 0 ? s : '—'}</Text>;
        },
      },
      {
        title: 'Почему подходит',
        key: 'reasoning',
        width: 260,
        render: (_: unknown, record: Angle) => (
          <Text style={{ fontSize: 12, color: record.reasoning ? '#595959' : '#bfbfbf', whiteSpace: 'normal', lineHeight: '1.5' }}>
            {record.reasoning || 'Не заполнено'}
          </Text>
        ),
      },
      {
        title: 'Объявл.',
        key: 'ads',
        width: 65,
        align: 'center' as const,
        render: (_: unknown, record: Angle) => {
          const cnt = countAdsAngle(record.id);
          return cnt > 0 ? <Tag>{cnt}</Tag> : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
        },
      },
    ];

    return (
      <>
        {currentAngles.length === 0
          ? <Empty description="Нет стилей." style={{ padding: '48px 0' }} />
          : <>
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {enabledCount} из {currentAngles.length} стилей включены для генерации
                </Text>
              </div>
              <Table
                rowKey="id"
                columns={columns}
                dataSource={currentAngles}
                pagination={false}
                size="small"
                scroll={{ x: 1100 }}
              />
            </>}
      </>
    );
  };

  const renderOffers = () => {
    if (!selectedUnit) return null;
    return offers.length === 0
      ? <Empty description="Нет офферов." style={{ padding: '48px 0' }} />
      : <div className="sm-cards">{offers.map((o, i) => renderCard(o.id, o.name, o.description, 'Нет объявлений', () => handleSelectOffer(o.id), undefined, undefined, <GiftOutlined />, pinnedOffer === o.id, () => handleTogglePinOffer(o.id), undefined, { background: '#fff7e6', color: '#fa8c16' }, i + 1))}</div>;
  };

  const renderPlatforms = () => {
    if (!selectedOffer) return null;
    const platforms: AdPlatform[] = ['yandex_direct', 'vk'];
    return (
      <div className="sm-cards">
        {platforms.map((pl) => {
          const cfg = PLATFORM_CONFIG[pl];
          const isPinned = pinnedPlatform === pl;
          return (
            <div key={pl} className={`sm-card${isPinned ? ' sm-card--pinned' : ''}`} onClick={() => handleSelectPlatform(pl)}>
              <div className="sm-card__body">
                <div className="sm-card__icon sm-card__icon--platform" style={{ background: pl === 'vk' ? '#e8f0fa' : '#fff8e1' }}>{cfg.icon}</div>
                <div className="sm-card__info"><Text strong className="sm-card__name">{cfg.label}</Text></div>
              </div>
              <div className="sm-card__meta">
                <Text type="secondary" style={{ fontSize: 12 }}>{ads.filter((a) => a.offerId === selectedOffer!.id && a.platform === pl).length > 0 ? `${ads.filter((a) => a.offerId === selectedOffer!.id && a.platform === pl).length} объявл.` : 'Нет объявлений'}</Text>
                <Space size={4} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Настройки генерации"><Button type="text" size="small" icon={<SettingOutlined />} onClick={() => setPlatformSettingsOpen(pl)} style={{ color: '#8c8c8c' }} /></Tooltip>
                  <Tooltip title={isPinned ? 'Открепить' : 'Закрепить'}><Button type="text" size="small" className={`sm-card__pin${isPinned ? ' sm-card__pin--active' : ''}`} icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />} onClick={() => handleTogglePinPlatform(pl)} /></Tooltip>
                </Space>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCabinets = () => {
    if (!selectedPlatform) return null;
    return (
      <>
        {cabinets.length === 0
          ? <Empty description={`Нет кабинетов для ${PLATFORM_CONFIG[selectedPlatform].label}. Добавьте.`} style={{ padding: '48px 0' }} />
          : <div className="sm-cards">{cabinets.map((cab) => {
              const cnt = ads.filter((a) => a.offerId === selectedOffer!.id && a.platform === selectedPlatform && a.cabinetId === cab.id).length;
              const isPinned = pinnedCabinet === cab.id;
              return (
                <div key={cab.id} className={`sm-card${isPinned ? ' sm-card--pinned' : ''}`} onClick={() => handleSelectCabinet(cab.id)}>
                  <div className="sm-card__body">
                    <div className="sm-card__icon sm-card__icon--platform" style={{ background: selectedPlatform === 'vk' ? '#e8f0fa' : '#fff8e1' }}><DesktopOutlined /></div>
                    <div className="sm-card__info">
                      <Text strong className="sm-card__name">{cab.name}</Text>
                      <Text type="secondary" className="sm-card__desc">ID: {cab.externalId}</Text>
                    </div>
                  </div>
                  <div className="sm-card__meta">
                    <Text type="secondary" style={{ fontSize: 12 }}>{cnt > 0 ? `${cnt} объявл.` : 'Нет объявлений'}</Text>
                    <Space size={4} onClick={(e) => e.stopPropagation()}>
                      <Popconfirm title="Убрать кабинет?" onConfirm={() => handleRemoveCabinet(cab.id)} okText="Да" cancelText="Нет">
                        <Tooltip title="Убрать"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
                      </Popconfirm>
                      <Tooltip title={isPinned ? 'Открепить' : 'Закрепить'}><Button type="text" size="small" className={`sm-card__pin${isPinned ? ' sm-card__pin--active' : ''}`} icon={isPinned ? <PushpinFilled /> : <PushpinOutlined />} onClick={() => handleTogglePinCabinet(cab.id)} /></Tooltip>
                    </Space>
                  </div>
                </div>
              );
            })}</div>}
        <div style={{ marginTop: 12 }}>
          <Button icon={<PlusOutlined />} onClick={() => setCabinetPickerVisible(true)} style={{ borderRadius: 8 }} disabled={availableCabinetsForPicker.length === 0}>
            Добавить кабинет
          </Button>
          {availableCabinetsForPicker.length === 0 && cabinets.length > 0 && (
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>Все кабинеты добавлены</Text>
          )}
        </div>
      </>
    );
  };

  const [cropState, setCropState] = useState<{ adId: string; dataUrl: string } | null>(null);

  const handleAdImageUpload = (adId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      // Check aspect ratio — if wider than 4:3, offer crop
      const img = new Image();
      img.onload = () => {
        const ratio = img.width / img.height;
        if (ratio > 4 / 3 + 0.05) {
          setCropState({ adId, dataUrl });
        } else {
          setAds((prev) => prev.map((a) => a.id === adId ? { ...a, image: dataUrl } : a));
          message.success('Изображение загружено');
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleCropComplete = (croppedDataUrl: string) => {
    if (!cropState) return;
    setAds((prev) => prev.map((a) => a.id === cropState.adId ? { ...a, image: croppedDataUrl } : a));
    setCropState(null);
    message.success('Изображение обрезано и загружено');
  };

  const handleAdImageRemove = (adId: string) => {
    setAds((prev) => prev.map((a) => a.id === adId ? { ...a, image: undefined, imageSettings: undefined } : a));
  };

  // --- Inline ad field update ---
  const updateAd = (adId: string, field: keyof Ad, value: string | AdSitelink[]) => {
    setAds((prev) => prev.map((a) => a.id === adId ? { ...a, [field]: value } : a));
  };
  const updateAdSitelink = (adId: string, idx: number, slField: keyof AdSitelink, value: string) => {
    setAds((prev) => prev.map((a) => {
      if (a.id !== adId) return a;
      const sl = [...a.sitelinks] as [AdSitelink, AdSitelink, AdSitelink, AdSitelink];
      sl[idx] = { ...sl[idx], [slField]: value };
      return { ...a, sitelinks: sl };
    }));
  };

  const renderAds = () => {
    if (!selectedPlatform || !selectedAngle || !selectedOffer || !selectedCabinet) return null;
    const plCfg = PLATFORM_CONFIG[selectedPlatform];
    const isYa = selectedPlatform === 'yandex_direct';

    type RowDef = { key: string; label: string; render: (ad: Ad) => React.ReactNode };
    const rows: RowDef[] = [];

    // --- Status ---
    rows.push({ key: 'status', label: 'Статус', render: (ad) => (
      <Select size="small" value={ad.status} onChange={(v) => updateAd(ad.id, 'status', v)} style={{ width: '100%', fontSize: 12 }}>
        {(Object.keys(AD_STATUS_CONFIG) as AdStatus[]).map((s) => <Select.Option key={s} value={s}><Tag color={AD_STATUS_CONFIG[s].color} style={{ fontSize: 11 }}>{AD_STATUS_CONFIG[s].label}</Tag></Select.Option>)}
      </Select>
    )});

    // --- Title 1 ---
    rows.push({ key: 'title', label: 'Заголовок 1 (56)', render: (ad) => <EditCell value={ad.title} max={56} onChange={(v) => updateAd(ad.id, 'title', v)} placeholder="Заголовок объявления" /> });

    // --- Title 2 ---
    if (isYa) rows.push({ key: 'title2', label: 'Заголовок 2 (30)', render: (ad) => <EditCell value={ad.title2} max={30} onChange={(v) => updateAd(ad.id, 'title2', v)} placeholder="Дополнительный заголовок" /> });

    // --- Text ---
    rows.push({ key: 'text', label: 'Текст (81)', render: (ad) => <EditCell value={ad.text} max={81} onChange={(v) => updateAd(ad.id, 'text', v)} multiline placeholder="Текст объявления" /> });

    // --- URL ---
    rows.push({ key: 'url', label: 'Ссылка (1024)', render: (ad) => <EditCell value={ad.url} max={1024} onChange={(v) => updateAd(ad.id, 'url', v)} placeholder="https://..." /> });

    // --- Display URL ---
    if (isYa) rows.push({ key: 'displayUrl', label: 'Отобр. ссылка (20)', render: (ad) => <EditCell value={ad.displayUrl} max={20} onChange={(v) => updateAd(ad.id, 'displayUrl', v)} placeholder="путь/после/домена" /> });

    // --- 4 Sitelinks ---
    if (isYa) {
      for (let i = 0; i < 4; i++) {
        const n = i + 1;
        rows.push({ key: `sl${n}_title`, label: `БС ${n} — загол. (30)`, render: (ad) => <EditCell value={ad.sitelinks[i].title} max={30} onChange={(v) => updateAdSitelink(ad.id, i, 'title', v)} placeholder={`Быстрая ссылка ${n}`} /> });
        rows.push({ key: `sl${n}_desc`, label: `БС ${n} — описание (60)`, render: (ad) => <EditCell value={ad.sitelinks[i].description} max={60} onChange={(v) => updateAdSitelink(ad.id, i, 'description', v)} placeholder="Описание" /> });
        rows.push({ key: `sl${n}_url`, label: `БС ${n} — ссылка`, render: (ad) => <EditCell value={ad.sitelinks[i].url} max={1024} onChange={(v) => updateAdSitelink(ad.id, i, 'url', v)} placeholder="https://..." /> });
      }
    }

    // --- Callouts ---
    rows.push({ key: 'callouts', label: 'Уточнения (25×4)', render: (ad) => <EditCell value={ad.callouts.join(', ')} max={132} onChange={(v) => updateAd(ad.id, 'callouts', v.split(',').map((s: string) => s.trim()).filter(Boolean) as unknown as string)} multiline placeholder="Через запятую" /> });

    // --- Action button ---
    rows.push({ key: 'actionButton', label: 'Кнопка действия', render: (ad) => (
      <Select size="small" value={ad.actionButton} onChange={(v) => updateAd(ad.id, 'actionButton', v)} style={{ width: '100%', fontSize: 12 }}>
        {['Перейти', 'Скачать', 'Купить', 'Записаться', 'Подробнее', 'Узнать больше', 'Зарегистрироваться', 'Заказать', 'Попробовать'].map((b) => <Select.Option key={b} value={b}>{b}</Select.Option>)}
      </Select>
    )});

    // --- Image text ---
    rows.push({ key: 'imageText', label: 'Текст на картинку', render: (ad) => <EditCell value={ad.imageText} max={100} onChange={(v) => updateAd(ad.id, 'imageText', v)} multiline placeholder="Текст поверх изображения" /> });

    // --- Image button text ---
    rows.push({ key: 'imageButtonText', label: 'Кнопка на картинку', render: (ad) => <EditCell value={ad.imageButtonText} max={40} onChange={(v) => updateAd(ad.id, 'imageButtonText', v)} placeholder="Текст кнопки" /> });

    // --- Image style settings ---
    rows.push({ key: 'imageStyle', label: 'Стиль картинки', render: (ad) => (
      <Button size="small" icon={<SettingOutlined />} onClick={() => setEditingImageAdId(ad.id)} disabled={!ad.image} style={{ fontSize: 12 }}>Настройки</Button>
    )});

    // --- Image ---
    rows.push({ key: 'image', label: 'Изображение', render: (ad) => ad.image ? (
      <div className="ads-table__img-wrap">
        <img src={ad.image} alt="" className="ads-table__img" />
        <div className="ads-table__img-actions">
          <label className="ads-table__img-btn"><UploadOutlined /> <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAdImageUpload(ad.id, f); }} /></label>
          <button className="ads-table__img-btn" onClick={() => handleAdImageRemove(ad.id)}><DeleteOutlined /></button>
        </div>
      </div>
    ) : (
      <label className="ads-table__img-upload">
        <UploadOutlined style={{ fontSize: 16, color: '#bfbfbf' }} />
        <span style={{ fontSize: 11, color: '#bfbfbf' }}>Загрузить</span>
        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAdImageUpload(ad.id, f); }} />
      </label>
    )});

    // --- Preview ---
    rows.push({ key: 'preview', label: 'Превью', render: (ad) => (
      <div className="ya-preview">
        {/* Image with badge */}
        <div className="ya-preview__img-section">
          {ad.image ? (
            <img src={ad.image} alt="" className="ya-preview__img" />
          ) : (
            <div className="ya-preview__img-placeholder" />
          )}
          <div className="ya-preview__badge">РЕКЛАМА · 16+</div>
          <div className="ya-preview__dots">•••</div>
        </div>
        {/* Content */}
        <div className="ya-preview__content">
          <div className="ya-preview__domain">
            <div className="ya-preview__favicon" />
            <span>{ad.displayUrl || ad.url}</span>
          </div>
          <div className="ya-preview__title">{ad.title}{ad.title2 ? `, ${ad.title2}` : ''}</div>
          <div className="ya-preview__text">{ad.text}</div>
          {ad.sitelinks.some((sl) => sl.title) && (
            <div className="ya-preview__sitelinks">
              {ad.sitelinks.filter((sl) => sl.title).map((sl, i) => (
                <div key={i} className="ya-preview__sitelink">
                  <span>{sl.title}</span>
                  <span className="ya-preview__sitelink-arrow">&gt;</span>
                </div>
              ))}
            </div>
          )}
          <div className="ya-preview__btn">{ad.actionButton || 'Перейти'}</div>
        </div>
      </div>
    )});

    // --- Delete ---
    rows.push({ key: 'actions', label: '', render: (ad) => (
      <Popconfirm title="Удалить?" onConfirm={() => setAds((prev) => prev.filter((a) => a.id !== ad.id))} okText="Да" cancelText="Нет">
        <Button type="text" size="small" danger icon={<DeleteOutlined />}>Удалить</Button>
      </Popconfirm>
    )});

    return (
      <div className="sm-ads">
        <div className="sm-ads__toolbar">
          <Button icon={<ThunderboltOutlined />} onClick={() => handleInlineGenerate(3)} style={{ borderRadius: 8 }}>Сгенерировать ещё 3</Button>
        </div>
        {currentAds.length === 0
          ? <Empty description={`Нет объявлений для ${plCfg.label}`} style={{ padding: '32px 0' }} />
          : <div className="ads-table-wrap">
              <table className="ads-table">
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <th className="ads-table__label">{row.label}</th>
                      {currentAds.map((ad) => (
                        <td key={ad.id} className="ads-table__cell">{row.render(ad)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}
      </div>
    );
  };

  // --- Nav tabs ---
  const pinnedOfferName = pinnedOffer ? offers.find((o) => o.id === pinnedOffer)?.name || null : null;
  const pinnedPlatformName = pinnedPlatform ? PLATFORM_CONFIG[pinnedPlatform].label : null;
  const pinnedCabinetName = pinnedCabinet ? allConnectedCabinets.find((c) => c.id === pinnedCabinet)?.name || null : null;

  const navTabs: { key: Level; label: string; icon: React.ReactNode; selectedName: string | null; pinnedName: string | null; enabled: boolean }[] = [
    { key: 'spheres', label: 'Сферы', icon: <AimOutlined className="sm-nav__tab-icon" />, selectedName: selectedSphere?.name || null, pinnedName: null, enabled: true },
    { key: 'themes', label: 'Темы', icon: <AppstoreOutlined className="sm-nav__tab-icon" />, selectedName: selectedTheme?.name || null, pinnedName: null, enabled: !!selectedSphere },
    { key: 'subthemes', label: 'Подтемы', icon: <AimOutlined className="sm-nav__tab-icon" />, selectedName: selectedSubtheme?.name || null, pinnedName: null, enabled: !!selectedTheme },
    { key: 'units', label: 'Смысловая единица', icon: <AimOutlined className="sm-nav__tab-icon" />, selectedName: selectedUnit?.name || null, pinnedName: null, enabled: !!selectedSubtheme },
    { key: 'offers', label: 'Офферы', icon: <GiftOutlined className="sm-nav__tab-icon" />, selectedName: selectedOffer?.name || null, pinnedName: pinnedOfferName, enabled: !!selectedUnit },
    { key: 'platforms', label: 'Площадки', icon: <DesktopOutlined className="sm-nav__tab-icon" />, selectedName: selectedPlatform ? PLATFORM_CONFIG[selectedPlatform].label : null, pinnedName: pinnedPlatformName, enabled: !!selectedOffer },
    { key: 'cabinets', label: 'Рекламные кабинеты', icon: <DesktopOutlined className="sm-nav__tab-icon" />, selectedName: selectedCabinet?.name || null, pinnedName: pinnedCabinetName, enabled: !!selectedPlatform },
    { key: 'angles', label: 'Стиль', icon: <BulbOutlined className="sm-nav__tab-icon" />, selectedName: selectedAngle ? ANGLE_TYPE_CONFIG[selectedAngle.type].label : null, pinnedName: null, enabled: !!selectedCabinet },
    { key: 'ads', label: 'Объявления', icon: <SoundOutlined className="sm-nav__tab-icon" />, selectedName: null, pinnedName: null, enabled: !!selectedAngle },
  ];

  const showAddButton = ['spheres', 'themes', 'subthemes', 'units'].includes(currentLevel);
  const modalTitle = !modalMode ? '' : `${modalMode.editing ? 'Редактировать' : 'Добавить'} ${modalMode.type === 'sphere' ? 'сферу' : modalMode.type === 'theme' ? 'категорию' : modalMode.type === 'subtheme' ? 'тему' : 'смысловую единицу'}`;
  const addBtnLabel = currentLevel === 'spheres' ? 'Добавить сферу' : currentLevel === 'themes' ? 'Добавить тему' : currentLevel === 'subthemes' ? 'Добавить подтему' : 'Добавить смысловую единицу';

  return (
    <div className="semantic-map">
      {/* Nav */}
      <div className="sm-nav">
        {navTabs.map((tab) => {
          const isActive = currentLevel === tab.key;
          const hasPinned = !!tab.pinnedName;
          const isClickable = tab.enabled || hasPinned;
          const isDisabled = !tab.enabled && !hasPinned;
          let cn = 'sm-nav__tab';
          if (isActive) cn += ' sm-nav__tab--active';
          if (isDisabled) cn += ' sm-nav__tab--disabled';
          if (!tab.enabled && hasPinned) cn += ' sm-nav__tab--pinned-only';
          return (
            <div key={tab.key} className={cn} onClick={isClickable && tab.key !== 'ads' ? () => handleClickTab(tab.key) : undefined}>
              <div className="sm-nav__tab-row">
                {tab.icon}
                <Text className="sm-nav__tab-label">{tab.label}</Text>
                {tab.pinnedName && !tab.selectedName && <PushpinFilled className="sm-nav__tab-pin-icon" />}
              </div>
              {tab.selectedName ? <div className="sm-nav__tab-chip">{tab.selectedName}</div>
                : tab.pinnedName ? <div className="sm-nav__tab-chip sm-nav__tab-chip--pinned"><PushpinFilled style={{ fontSize: 10, marginRight: 4 }} />{tab.pinnedName}</div>
                : null}
            </div>
          );
        })}
      </div>

      {/* Action bar */}
      <div className="sm-action-bar">
        <div className="sm-action-bar__left">
          {currentLevel !== 'ads' && (
            <Radio.Group value={smViewMode} onChange={(e) => setSmViewMode(e.target.value)} size="middle" className="sm-view-toggle">
              <Radio.Button value="cards"><AppstoreOutlined /></Radio.Button>
              <Radio.Button value="table"><UnorderedListOutlined /></Radio.Button>
            </Radio.Group>
          )}
        </div>
        <div className="sm-action-bar__right">
          <Button icon={<ThunderboltOutlined />} onClick={() => setAutoModeOpen(true)} style={{ borderRadius: 8 }}>Авто-режим</Button>
          <Button icon={<ThunderboltOutlined />} onClick={() => setGenerateModalVisible(true)} style={{ borderRadius: 8 }}>Генерация объявлений</Button>
          {showAddButton && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', borderRadius: 8 }}>{addBtnLabel}</Button>
          )}
        </div>
      </div>

      {/* Content */}
      {smViewMode === 'cards' || currentLevel === 'ads' ? (
        <>
          {currentLevel === 'spheres' && renderSpheres()}
          {currentLevel === 'themes' && renderThemes()}
          {currentLevel === 'subthemes' && renderSubthemes()}
          {currentLevel === 'units' && renderUnits()}
          {currentLevel === 'offers' && renderOffers()}
          {currentLevel === 'platforms' && renderPlatforms()}
          {currentLevel === 'cabinets' && renderCabinets()}
          {currentLevel === 'angles' && renderAngles()}
          {currentLevel === 'ads' && renderAds()}
        </>
      ) : (
        <div className="sm-table-wrap">
          {currentLevel === 'spheres' && renderSpheresTable()}
          {currentLevel === 'themes' && renderThemesTable()}
          {currentLevel === 'subthemes' && renderSubthemesTable()}
          {currentLevel === 'units' && renderUnitsTable()}
          {currentLevel === 'offers' && renderOffersTable()}
          {currentLevel === 'platforms' && renderPlatformsTable()}
          {currentLevel === 'cabinets' && renderCabinetsTable()}
        </div>
      )}

      {/* CRUD Modal */}
      <Modal title={modalTitle} visible={!!modalMode} onOk={handleSubmit} onCancel={() => { setModalMode(null); form.resetFields(); }} okText={modalMode?.editing ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={520} destroyOnClose>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder={modalMode?.type === 'sphere' ? 'Например: Карьера и рост' : modalMode?.type === 'theme' ? 'Например: Смена профессии' : modalMode?.type === 'subtheme' ? 'Например: Страх перемен' : 'Например: Страх бездействия'} />
          </Form.Item>
          <Form.Item name="description" label="Описание"><TextArea rows={2} placeholder="Краткое описание" /></Form.Item>
          <Space.Compact block style={{ marginBottom: 16 }}>
            <Form.Item name="status" label="Статус" style={{ flex: 1, marginBottom: 0, marginRight: 12 }} initialValue="draft">
              <Select
                options={(Object.keys(NODE_STATUS_CONFIG) as NodeStatus[]).map((k) => ({
                  value: k,
                  label: <Tag color={NODE_STATUS_CONFIG[k].color} style={{ margin: 0 }}>{NODE_STATUS_CONFIG[k].label}</Tag>,
                }))}
              />
            </Form.Item>
            <Form.Item name="priority" label="Приоритет (1-10)" style={{ flex: 2, marginBottom: 0 }} initialValue={5}>
              <Slider min={0} max={10} step={1} marks={{ 0: '0', 5: '5', 10: '10' }} />
            </Form.Item>
          </Space.Compact>
          {modalMode?.type === 'theme' && (
            <Form.Item name="images" label="Картинки (URL)" extra="Каждый URL с новой строки. Картинки наследуются объявлениями."><TextArea rows={2} placeholder={'https://example.com/image1.jpg\nhttps://example.com/image2.jpg'} /></Form.Item>
          )}
          {(modalMode?.type === 'subtheme' || modalMode?.type === 'unit') && (
            <Form.Item name="keywords" label="Ключевые слова" extra="Каждое ключевое слово с новой строки"><TextArea rows={3} placeholder={'похудение\nснижение веса\nкак похудеть'} /></Form.Item>
          )}
          {(modalMode?.type === 'subtheme' || modalMode?.type === 'unit') && (
            <Form.Item name="negativeKeywords" label="Минус-слова" extra="Каждое минус-слово с новой строки"><TextArea rows={3} placeholder={'бесплатно\nреферат\nскачать'} /></Form.Item>
          )}
          {modalMode?.type === 'unit' && (
            <Form.Item name="examples" label="Примеры фраз" extra="Каждая фраза с новой строки"><TextArea rows={3} placeholder={'А вдруг не получится?\nМне уже 35, поздно менять'} /></Form.Item>
          )}
        </Form>
      </Modal>

      {/* Generate Modal */}
      <GenerateAdsModal visible={generateModalVisible} onClose={() => setGenerateModalVisible(false)} onGenerate={(newAds) => { setAds((prev) => [...prev, ...newAds]); message.success(`Сгенерировано ${newAds.length} объявлений`); }} spheres={spheres} angles={angles} offers={offers} productId={productId} />

      {/* Auto Mode Setup */}
      <AutoModeSetupModal
        visible={autoModeOpen}
        onClose={() => setAutoModeOpen(false)}
        productId={productId}
        spheres={spheres}
        cabinets={allConnectedCabinets}
        productCabinetIds={productCabinetIds}
      />

      {/* Cabinet Picker */}
      <CabinetPickerModal visible={cabinetPickerVisible} onClose={() => setCabinetPickerVisible(false)} onAdd={handleAddCabinets} cabinets={availableCabinetsForPicker} platformLabel={selectedPlatform ? PLATFORM_CONFIG[selectedPlatform].label : ''} />

      {/* Platform Settings Drawer */}
      <Drawer title={platformSettingsOpen === 'yandex_direct' ? 'Настройки Яндекс Директ' : platformSettingsOpen === 'vk' ? 'Настройки ВКонтакте' : ''} visible={!!platformSettingsOpen} onClose={() => setPlatformSettingsOpen(null)} width={560} bodyStyle={{ paddingTop: 12 }}>
        {platformSettingsOpen === 'yandex_direct' && <YandexDirectSettings settings={yaSettings} onChange={setYaSettings} />}
        {platformSettingsOpen === 'vk' && <Empty description="Настройки для VK будут добавлены позже" style={{ padding: '48px 0' }} />}
      </Drawer>

      {/* Unit Preview Drawer (Вариант 3: превью СЕ) */}
      <Drawer
        title={previewUnitId ? `Превью СЕ: ${(() => { for (const s of spheres) for (const c of s.themes) for (const t of c.subthemes) { const u = t.units.find((u) => u.id === previewUnitId); if (u) return u.name; } return ''; })()}` : ''}
        visible={!!previewUnitId}
        onClose={() => setPreviewUnitId(null)}
        width={600}
        bodyStyle={{ paddingTop: 12 }}
      >
        {previewUnitId && (() => {
          let unit: { name: string; description: string; keywords?: string[] } | null = null;
          for (const s of spheres) for (const c of s.themes) for (const t of c.subthemes) { const u = t.units.find((u) => u.id === previewUnitId); if (u) { unit = u; break; } }
          if (!unit) return <Empty description="СЕ не найдена" />;
          const unitAngles = angles.filter((a) => a.unitId === previewUnitId);
          const unitAds = ads.filter((a) => unitAngles.some((ang) => ang.id === a.angleId));
          const campCount = mockUnitCampaignCount[previewUnitId] || 0;

          return (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Описание</Text>
                <div>{unit.description || '—'}</div>
              </div>

              {unit.keywords && unit.keywords.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Ключевые слова</Text>
                  <div style={{ marginTop: 4 }}>
                    {unit.keywords.map((kw, i) => <Tag key={i} style={{ marginBottom: 4 }}>{kw}</Tag>)}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Стили ({unitAngles.length})</Text>
                <div style={{ marginTop: 4 }}>
                  {unitAngles.length === 0 ? <Text type="secondary">Нет назначенных стилей</Text> : unitAngles.map((a) => {
                    const cfg = ANGLE_TYPE_CONFIG[a.type];
                    return cfg ? <Tag key={a.id} style={{ marginBottom: 4 }}>{cfg.label}</Tag> : null;
                  })}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Кампании</Text>
                <div style={{ marginTop: 4 }}>
                  {campCount === 0
                    ? <Text type="secondary">Не используется в кампаниях</Text>
                    : <Tag color="blue">Используется в {campCount} кампаниях</Tag>}
                </div>
              </div>

              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Объявления ({unitAds.length})</Text>
                {unitAds.length === 0 ? (
                  <div style={{ marginTop: 4 }}><Text type="secondary">Нет сгенерированных объявлений</Text></div>
                ) : (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {unitAds.slice(0, 5).map((ad) => (
                      <div key={ad.id} style={{ padding: '10px 12px', border: '1px solid #f0f0f0', borderRadius: 8, background: '#fff' }}>
                        <div style={{ fontWeight: 600, color: '#1a0dab', fontSize: 14, marginBottom: 2 }}>{ad.title}</div>
                        {ad.title2 && <div style={{ color: '#1a0dab', fontSize: 13, marginBottom: 4 }}>{ad.title2}</div>}
                        <div style={{ fontSize: 12, color: '#545454' }}>{ad.text}</div>
                      </div>
                    ))}
                    {unitAds.length > 5 && <Text type="secondary" style={{ fontSize: 12 }}>...и ещё {unitAds.length - 5}</Text>}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Drawer>

      {/* Ad Image Editor */}
      {editingImageAdId && (() => {
        const editingAd = ads.find((a) => a.id === editingImageAdId);
        if (!editingAd || !editingAd.image) return null;
        return (
          <AdImageEditor
            visible={true}
            imageSrc={editingAd.image}
            settings={editingAd.imageSettings || { ...DEFAULT_AD_IMAGE_SETTINGS, overlayText: editingAd.title }}
            adTitle={editingAd.title}
            onClose={() => setEditingImageAdId(null)}
            onSave={(imgSettings) => {
              setAds((prev) => prev.map((a) => a.id === editingImageAdId ? { ...a, imageSettings: imgSettings } : a));
              message.success('Настройки изображения сохранены');
            }}
            onImageChange={(file) => handleAdImageUpload(editingImageAdId, file)}
          />
        );
      })()}

      {/* Crop Modal */}
      <CropModal
        visible={!!cropState}
        imageSrc={cropState?.dataUrl || ''}
        onCropComplete={handleCropComplete}
        onCancel={() => setCropState(null)}
      />

      {/* Keywords View Modal */}
      <Modal
        title={(() => {
          if (!keywordsViewKey) return '';
          if (keywordsViewKey.type === 'subtheme') {
            for (const s of spheres) for (const c of s.themes) {
              const t = c.subthemes.find((th) => th.id === keywordsViewKey.id);
              if (t) return `Ключевые и минус-слова темы «${t.name}»`;
            }
          } else {
            for (const s of spheres) for (const c of s.themes) for (const t of c.subthemes) {
              const u = t.units.find((un) => un.id === keywordsViewKey.id);
              if (u) return `Ключевые и минус-слова СЕ «${u.name}»`;
            }
          }
          return '';
        })()}
        visible={!!keywordsViewKey}
        onCancel={() => setKeywordsViewKey(null)}
        footer={<Button onClick={() => setKeywordsViewKey(null)}>Закрыть</Button>}
        width={720}
        destroyOnClose
      >
        {keywordsViewKey && (() => {
          let entity: { keywords?: string[]; negativeKeywords?: string[] } | null = null;
          if (keywordsViewKey.type === 'subtheme') {
            for (const s of spheres) for (const c of s.themes) {
              const t = c.subthemes.find((th) => th.id === keywordsViewKey.id);
              if (t) { entity = t; break; }
            }
          } else {
            for (const s of spheres) for (const c of s.themes) for (const t of c.subthemes) {
              const u = t.units.find((un) => un.id === keywordsViewKey.id);
              if (u) { entity = u; break; }
            }
          }
          if (!entity) return <Empty description="Не найдено" />;
          const kw = entity.keywords || [];
          const neg = entity.negativeKeywords || [];
          return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <Text strong>Ключевые слова <Text type="secondary">({kw.length})</Text></Text>
                {kw.length === 0 ? <Empty description="Нет ключевых слов" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} /> : (
                  <Table
                    rowKey={(r: { kw: string }) => r.kw}
                    dataSource={kw.map((k) => ({ kw: k }))}
                    pagination={false}
                    size="small"
                    columns={[ { title: 'Фраза', dataIndex: 'kw', key: 'kw' } ]}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
              <div>
                <Text strong>Минус-слова <Text type="secondary">({neg.length})</Text></Text>
                {neg.length === 0 ? <Empty description="Нет минус-слов" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} /> : (
                  <Table
                    rowKey={(r: { kw: string }) => r.kw}
                    dataSource={neg.map((k) => ({ kw: k }))}
                    pagination={false}
                    size="small"
                    columns={[ { title: 'Фраза', dataIndex: 'kw', key: 'kw' } ]}
                    style={{ marginTop: 8 }}
                  />
                )}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default SemanticMapTab;
